import React, { useRef, useState, useEffect, createContext, useMemo } from "react";
import { LoadScript } from "@react-google-maps/api";
import Split from "react-split";
import WebMapGoogleMap from "./WebMapGoogleMap";
import { MapTools, geohashDefaultZoomLvl, sitesDefaultZoomLvl } from "../../../constants/WebMap";
import { WebMapLogger } from "../../../utils/webMapLogger";

export const LoadMapContext = createContext({});

const LoadWebMap = () => {
  const logger = WebMapLogger.getInstance();
  const leftMapRef = useRef();
  const rightMapRef = useRef();
  let center, zoom;
  const [width, setWidth] = useState();
  const [mapView, setMapView] = useState(null);
  const [showMsModal, setShowMsModal] = useState({
    leftModal: true,
    rightModal: false,
  });
  const [collapseSideBar, setCollapseSideBar] = useState({
    leftSideBar: false,
    rightSideBar: true,
  });
  const [showIbsTable, setShowIbsTable] = useState({
    leftTable: false,
    rightTable: false,
  });
  const [isIbsTableOpen, setIsIbsTableOpen] = useState(false);
  const [isOpMsTableOpen, setIsOpMsTableOpen] = useState(false);
  const [sidePanelWidth, setSidePanelWidth] = useState("");
  const [commonFilters, setCommonFilters] = useState({
    country: null,
    countryCode: null,
    level: null,
    levelVal: null,
    year: null,
    quarter: null,
    adminLevel1: null,
    adminLevel2: null,
    adminLevel3: null,
    geohash: null
  });
  const [commonEyeIcons, setCommonEyeIcons] = useState({
    adminLevel: false,
    supply: false,
    demand: false,
    sites: false,
    capacity: false
  });
  const [isMapsCoupled, setIsMapsCoupled] = useState(true);
  const [commonZoomLvls, setCommonZoomLvls] = useState({ geohash: geohashDefaultZoomLvl, sites: sitesDefaultZoomLvl })

  useEffect(() => {
    const element = document.getElementsByClassName('web-map-div')[0];
    if (element) {
      setWidth(element.offsetWidth);
      window.addEventListener("resize", () => setWidth(element.offsetWidth));
      return () => {
        window.removeEventListener("resize", () => setWidth(element.offsetWidth));
      };
    }
  }, []);

  useEffect(() => {
    if (leftMapRef.current && rightMapRef.current) {
      if ((isMapsCoupled || mapView === MapTools.SLIDER) && (leftMapRef.current.getCenter() != rightMapRef.current.getCenter())) {
        rightMapRef.current?.setCenter(leftMapRef.current.getCenter());
        rightMapRef.current?.setZoom(leftMapRef.current.getZoom());
      }
    }
  }, [isMapsCoupled, mapView]);

  useEffect(() => {
    setIsMapsCoupled(true);
    const { country } = commonFilters;
    logger.logPageChange(country ? "webmap - " + country : null);
  }, [commonFilters.country]);

  useEffect(() => {
    logger.scheduler();
    setInterval(() => logger.scheduler(), 900000); // 15 min
    return () => logger.updateLogEnd();
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', () => logger.updateLogEnd());
    return () => {
      window.removeEventListener('beforeunload', () => logger.updateLogEnd());
    };
  }, []);

  const isSync = () => isMapsCoupled || mapView === MapTools.SLIDER;
  const shouldSync = useMemo(() => isSync(), [isMapsCoupled, mapView]);

  return (
    <LoadMapContext.Provider
      value={{
        mapView,
        setMapView,
        commonFilters,
        setIsMapsCoupled,
        isMapsCoupled,
        commonEyeIcons,
        showMsModal,
        setShowMsModal,
        sidePanelWidth,
        setSidePanelWidth,
        collapseSideBar,
        setCollapseSideBar,
        showIbsTable,
        setShowIbsTable,
        isIbsTableOpen,
        setIsIbsTableOpen,
        isOpMsTableOpen,
        setIsOpMsTableOpen,
      }}
    >
      <div className="web-map-div">
        <LoadScript
          id="script-loader"
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
          language="en"
          region="us"
        >
          <Split style={{ display: "flex", height: "88vh" }} sizes={mapView === null ? [100] : [50, 50]} gutterSize={mapView === MapTools.SLIDER ? 10 : 0} direction="horizontal">
            <div id="map-left" style={{ position: "relative", overflow: mapView === MapTools.SLIDER ? "hidden" : "visible", float: "left", backgroundColor: "#8da787" }}>
              <div style={{ position: "absolute", left: 0, top: 0, width: `${mapView === MapTools.SPLIT ? (width / 2) - 2 : width}px` }}>
                <WebMapGoogleMap
                  initMap={(map) => leftMapRef.current = map}
                  mapRef={leftMapRef}
                  updateCenter={(c) => {
                    if (c != center) {
                      center = c;
                      if (shouldSync) rightMapRef.current?.setCenter(c);
                    }
                  }}
                  updateZoom={(z) => {
                    if (z != zoom) {
                      zoom = z;
                      if (shouldSync) rightMapRef.current?.setZoom(z);
                    }
                  }}
                  setCommonLocationLevels={setCommonFilters}
                  setCommonEyeIcons={setCommonEyeIcons}
                  setCommonZoomLvls={setCommonZoomLvls}
                  mapPlacement={'left'}
                />
              </div>
            </div>
            <div id="map-right" style={{ display: mapView === null ? "none" : "block", position: "relative", "overflow": "hidden", "float": "left", backgroundColor: "#8da787" }}>
              <div style={{ position: "absolute", right: 0, top: 0, width: `${mapView === MapTools.SPLIT ? (width / 2) - 2 : width}px` }}>
                {mapView !== null
                  ? <WebMapGoogleMap
                    initMap={(map) => rightMapRef.current = map}
                    mapRef={rightMapRef}
                    initMapSettings={{
                      zoom: leftMapRef.current.getZoom(),
                      center: { lat: leftMapRef.current.getCenter().lat(), lng: leftMapRef.current.getCenter().lng() }
                    }}
                    updateCenter={(c) => {
                      if (c != center) {
                        center = c;
                        if (shouldSync && mapView !== null) leftMapRef.current?.setCenter(c);
                      }
                    }}
                    updateZoom={(z) => {
                      if (z != zoom) {
                        zoom = z;
                        if (shouldSync && mapView !== null) leftMapRef.current?.setZoom(z);
                      }
                    }}
                    mapPlacement={'right'}
                    commonZoomLvls={commonZoomLvls}
                  />
                  : <></>}
              </div>
            </div>
          </Split>
        </LoadScript>
      </div>
    </LoadMapContext.Provider>
  )
};

export default LoadWebMap;